import { useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';

import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { BaseOption } from 'ui/selects';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { useToastNotification } from 'utils/hooks/useToastNotification';

import { useListsContext } from '../../contexts/ListsContext';
import { List } from '../../domain';
import { useUserDisplayName } from '../../hooks';
import { useListModalContext } from '../context/ListModalContext';
import { ModalOptions, ListAddOptions } from '../types';
import { ListSelect } from './ListSelect';

const BoldText = styled.b`
  font-weight: bold;
`;

export const displayContent = {
  empty: 'There are currently no lists to subscribe to',
  header: 'Add user to list',
  button: 'Add to list',
};

const isAddUserToListModal = (modalOption?: ModalOptions): modalOption is ListAddOptions =>
  (modalOption as ListAddOptions | undefined)?.toList !== undefined;

export const AddUserToListModal = () => {
  const userDisplayName = useUserDisplayName();
  const [selectedList, setSelectedList] = useState<List>();

  const { closeModal, listModalOptions } = useListModalContext();
  const {
    allLists,
    isLoading,
    isSubscribedToList,
    subscribe,
    resetSubscribe,
    isSubscribing,
    didSubscribe,
    couldNotSubscribe,
  } = useListsContext();

  const dropdownLists = useMemo(
    () =>
      allLists
        .filter((list) => !isSubscribedToList[list.id])
        .filter((list) => list.emailListType !== 'Dynamic'),
    [isSubscribedToList, allLists],
  );

  const handleClose = useCallback(() => {
    closeModal();
    setSelectedList(undefined);
    resetSubscribe();
  }, [closeModal, resetSubscribe]);

  useToastNotification({
    enabled: selectedList && (didSubscribe || couldNotSubscribe),
    negative: couldNotSubscribe,
    positive: didSubscribe,
    onDone: handleClose,
    negativeMessage: (
      <>
        Could not add <BoldText>{userDisplayName}</BoldText> to{' '}
        <BoldText>{selectedList?.name}</BoldText>
      </>
    ),
    positiveMessage: (
      <>
        Added <BoldText>{userDisplayName}</BoldText> to <BoldText>{selectedList?.name}</BoldText>
      </>
    ),
  });

  if (!isAddUserToListModal(listModalOptions)) return null;

  const listOptions: BaseOption[] = dropdownLists.map((list) => ({
    id: `${list.id}`,
    value: list.name,
    label: list.name,
  }));

  const handleSelect = (name: string) => {
    setSelectedList(dropdownLists.find((list) => list.name === name));
  };

  const handleSubmit = () => {
    if (selectedList) subscribe(selectedList);
  };

  return (
    <Modal
      id="list-subscribe-modal"
      withPortal
      onClose={handleClose}
      isModalOpen
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{displayContent.header}</ModalHeader>
      <ModalBody>
        <ListSelect
          listOptions={listOptions}
          isLoading={isLoading}
          selectOption={handleSelect}
          defaultName={
            isLoading || dropdownLists.length === 0 ? displayContent.empty : selectedList?.name
          }
        />
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={handleSubmit}
          isDisabled={isLoading || dropdownLists.length === 0 || !selectedList || isSubscribing}
          size={BUTTON_SIZES.LARGE}
        >
          {displayContent.button}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
